'use client';

import React, { useEffect, useRef } from 'react';

type GraphNode = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  pulse: number;
};

type StarParticle = {
  x: number;
  y: number;
  speed: number;
  size: number;
  opacity: number;
  drift: number;
};

type ShootingStar = {
  x: number;
  y: number;
  angle: number;
  speed: number;
  length: number;
  life: number;
  maxLife: number;
};

type RgbTuple = [number, number, number];

const hexToRgb = (value: string, fallback: RgbTuple): RgbTuple => {
  const trimmed = value.trim();
  if (!trimmed) return fallback;

  if (trimmed.startsWith('#')) {
    let hex = trimmed.slice(1);
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((char) => char + char)
        .join('');
    }
    if (hex.length === 6) {
      const num = parseInt(hex, 16);
      return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
    }
  }

  if (trimmed.startsWith('rgb')) {
    const matches = trimmed.match(/\d+/g);
    if (matches && matches.length >= 3) {
      return [Number(matches[0]), Number(matches[1]), Number(matches[2])];
    }
  }

  return fallback;
};

const rgba = (rgb: RgbTuple, alpha: number) => `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;

const AnimatedBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const nodesRef = useRef<GraphNode[]>([]);
  const starsRef = useRef<StarParticle[]>([]);
  const shootingStarsRef = useRef<ShootingStar[]>([]);
  const pointerRef = useRef({ x: 0, y: 0, active: false });
  const colorsRef = useRef({
    node: [99, 102, 241] as RgbTuple,
    edge: [99, 102, 241] as RgbTuple,
    star: [255, 255, 255] as RgbTuple,
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const pointer = pointerRef.current;
    const colors = colorsRef.current;
    const dpr = window.devicePixelRatio || 1;
    let width = window.innerWidth;
    let height = window.innerHeight;
    let colorTick = 0;

    const refreshColors = () => {
      const styles = getComputedStyle(document.documentElement);
      const primary = styles.getPropertyValue('--color-primary') || '#6366F1';
      const accent = styles.getPropertyValue('--color-accent') || '#8B5CF6';
      colors.edge = hexToRgb(primary, colors.edge);
      colors.node = hexToRgb(accent, colors.node);
      colors.star = hexToRgb(accent, colors.star);
    };

    const resizeCanvas = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const spawnNodes = () => {
      const baseCount = Math.floor((width * height) / 18000);
      const count = Math.min(70, Math.max(30, baseCount));
      nodesRef.current = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
        radius: 1 + Math.random() * 1.5,
        pulse: Math.random() * Math.PI * 2,
      }));
    };

    const spawnStars = () => {
      const count = 40;
      starsRef.current = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        speed: 0.05 + Math.random() * 0.12,
        size: 0.8 + Math.random() * 1.2,
        opacity: 0.05 + Math.random() * 0.15,
        drift: Math.random() * Math.PI * 2,
      }));
      shootingStarsRef.current = [];
    };

    const createShootingStar = (): ShootingStar => {
      const startFromLeft = Math.random() > 0.5;
      const x = startFromLeft ? -80 : Math.random() * width;
      const y = startFromLeft ? Math.random() * height * 0.5 : -80;
      const angle = startFromLeft
        ? 0.35 + Math.random() * 0.25 // shallow diagonal from left to right
        : 0.9 + Math.random() * 0.25; // sweeping from top-right toward bottom-left
      return {
        x,
        y,
        angle,
        speed: 0.6 + Math.random() * 0.8,
        length: 60 + Math.random() * 30,
        life: 0,
        maxLife: 0,
      };
    };

    const handleResize = () => {
      resizeCanvas();
      spawnNodes();
      spawnStars();
    };

    const handleMouseMove = (event: MouseEvent) => {
      pointer.x = event.clientX;
      pointer.y = event.clientY;
      pointer.active = true;
    };

    const handleMouseLeave = () => {
      pointer.active = false;
    };

    const drawStars = () => {
      const stars = starsRef.current;
      for (const star of stars) {
        star.y += star.speed;
        star.drift += 0.005;
        star.x += Math.sin(star.drift) * 0.1;

        if (star.y > height + 10) {
          star.y = -10;
          star.x = Math.random() * width;
        } else if (star.x > width + 10) {
          star.x = -10;
        } else if (star.x < -10) {
          star.x = width + 10;
        }

        ctx.fillStyle = rgba(colors.star, star.opacity);
        ctx.fillRect(star.x, star.y, star.size, star.size);
      }
    };

    const drawShootingStars = () => {
      const shootingStars = shootingStarsRef.current;

      if (shootingStars.length < 3 && Math.random() < 0.03) {
        shootingStars.push(createShootingStar());
      }

      for (let i = shootingStars.length - 1; i >= 0; i--) {
        const star = shootingStars[i];
        star.x += Math.cos(star.angle) * star.speed;
        star.y += Math.sin(star.angle) * star.speed;
        star.life += 1;

        const tailX = star.x - Math.cos(star.angle) * star.length;
        const tailY = star.y - Math.sin(star.angle) * star.length;

        const gradient = ctx.createLinearGradient(star.x, star.y, tailX, tailY);
        gradient.addColorStop(0, rgba(colors.star, 0.65));
        gradient.addColorStop(0.4, rgba(colors.star, 0.3));
        gradient.addColorStop(1, rgba(colors.star, 0));

        ctx.strokeStyle = gradient;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(star.x, star.y);
        ctx.lineTo(tailX, tailY);
        ctx.stroke();

        ctx.fillStyle = rgba(colors.star, 0.7);
        ctx.beginPath();
        ctx.arc(star.x, star.y, 1.2, 0, Math.PI * 2);
        ctx.fill();

        if (
          star.x > width + 100 ||
          star.x < -100 ||
          star.y > height + 100 ||
          star.y < -100
        ) {
          shootingStars.splice(i, 1);
        }
      }
    };

    const drawGraph = () => {
      const nodes = nodesRef.current;
      const maxLinkDistance = 190;
      const pointerLinkDistance = 130;

      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        node.x += node.vx;
        node.y += node.vy;
        node.pulse += 0.015;

        if (node.x < -20) node.x = width + 20;
        if (node.x > width + 20) node.x = -20;
        if (node.y < -20) node.y = height + 20;
        if (node.y > height + 20) node.y = -20;

        ctx.beginPath();
        ctx.arc(
          node.x,
          node.y,
          node.radius + Math.sin(node.pulse) * 0.3,
          0,
          Math.PI * 2
        );
        ctx.fillStyle = rgba(colors.node, 0.5);
        ctx.fill();

        for (let j = i + 1; j < nodes.length; j++) {
          const other = nodes[j];
          const dx = node.x - other.x;
          const dy = node.y - other.y;
          const distance = Math.hypot(dx, dy);

          if (distance < maxLinkDistance) {
            const alpha = (1 - distance / maxLinkDistance) * 0.45;
            ctx.strokeStyle = rgba(colors.edge, alpha);
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(other.x, other.y);
            ctx.stroke();
          }
        }

        if (pointer.active) {
          const dx = node.x - pointer.x;
          const dy = node.y - pointer.y;
          const pointerDistance = Math.hypot(dx, dy);

          if (pointerDistance < pointerLinkDistance) {
            const alpha = (1 - pointerDistance / pointerLinkDistance) * 0.55;
            ctx.strokeStyle = rgba(colors.edge, Math.min(0.75, alpha));
            ctx.lineWidth = 1.2;
            ctx.beginPath();
            ctx.moveTo(pointer.x, pointer.y);
            ctx.lineTo(node.x, node.y);
            ctx.stroke();
          }
        }
      }

      if (pointer.active) {
        const isLight = document.documentElement.classList.contains('light');
        const glowRadius = isLight ? 40 : 70;
        const gradient = ctx.createRadialGradient(
          pointer.x,
          pointer.y,
          0,
          pointer.x,
          pointer.y,
          glowRadius
        );
        gradient.addColorStop(0, rgba(colors.node, 0.18));
        gradient.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(pointer.x, pointer.y, glowRadius, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const render = () => {
      colorTick += 1;
      if (colorTick % 120 === 0) {
        refreshColors();
      }

      ctx.clearRect(0, 0, width, height);
      drawStars();
      drawShootingStars();
      drawGraph();
      animationRef.current = requestAnimationFrame(render);
    };

    resizeCanvas();
    refreshColors();
    spawnNodes();
    spawnStars();
    animationRef.current = requestAnimationFrame(render);

    const observer = new MutationObserver(refreshColors);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <>
      <div data-component-id="src/components/AnimatedBackground.tsx:359:6" data-component-path="src/components/AnimatedBackground.tsx" data-component-line="359" data-component-end-line="359" data-component-file="AnimatedBackground.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animated-gradient-bg%22%7D" className="animated-gradient-bg" aria-hidden="true" />
      <canvas data-component-id="src/components/AnimatedBackground.tsx:360:6" data-component-path="src/components/AnimatedBackground.tsx" data-component-line="360" data-component-end-line="360" data-component-file="AnimatedBackground.tsx" data-component-name="canvas" data-component-content="%7B%22elementName%22%3A%22canvas%22%2C%22className%22%3A%22graph-network-canvas%22%7D" ref={canvasRef} className="graph-network-canvas" aria-hidden="true" />
    </>
  );
};

export default AnimatedBackground;